import { useEffect, useRef, useState } from "react"
import type { User } from "@shared/types.ts"
import { googleLogin } from "../lib/api-client.ts"

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
            auto_select?: boolean
          }) => void
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: string
              size?: string
              shape?: string
              width?: number
              text?: string
            },
          ) => void
        }
      }
    }
  }
}

interface LoginScreenProps {
  onLogin: (user: User) => void
}

export function LoginScreen({ onLogin }: LoginScreenProps) {
  const buttonRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [gsiReady, setGsiReady] = useState(false)

  useEffect(() => {
    // Poll for Google Identity Services to load
    const interval = setInterval(() => {
      if (window.google?.accounts?.id) {
        setGsiReady(true)
        clearInterval(interval)
      }
    }, 100)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!gsiReady || !buttonRef.current) return

    const clientId = (window as Record<string, unknown>).__GOOGLE_CLIENT_ID__ as string | undefined

    if (!clientId) {
      // Fetch client ID from server config endpoint
      fetch("/api/auth/config")
        .then((res) => res.json())
        .then((data: { clientId: string }) => {
          initializeGoogle(data.clientId)
        })
        .catch(() => {
          setError("Failed to load auth configuration")
        })
      return
    }

    initializeGoogle(clientId)
  }, [gsiReady])

  function initializeGoogle(clientId: string) {
    if (!window.google?.accounts?.id || !buttonRef.current) return

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response) => {
        setError(null)
        try {
          const user = await googleLogin(response.credential)
          onLogin(user)
        } catch (err) {
          setError(err instanceof Error ? err.message : "Login failed")
        }
      },
    })

    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: "outline",
      size: "large",
      shape: "rectangular",
      width: 280,
      text: "signin_with",
    })
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-brand">
          <h1 className="login-title">cword</h1>
          <p className="login-subtitle">AI-powered crossword puzzles</p>
        </div>

        <div className="login-divider" />

        <div className="login-actions">
          <div ref={buttonRef} className="google-btn-container" />
          {!gsiReady && (
            <p className="login-loading">Loading sign-in…</p>
          )}
          {error && <p className="login-error">{error}</p>}
        </div>

        <p className="login-footer">
          Sign in to generate and solve crossword puzzles
        </p>
      </div>
    </div>
  )
}
